import classNames from 'clsx';
import { ReactComponent as IconArrow } from 'img/icons/dropArrow.svg';
import React, { memo, ReactElement } from 'react';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Breadcrumb } from 'reactApp/ui/Breadcrumbs/Breadcrumb';
import { noop } from 'reactApp/utils/helpers';

import styles from './Breadcrumbs.css';
import { BreadcrumbItem } from './Breadcrumbs.types';
import { BreadcrumbsDropdown } from './BreadcrumbsDropdown';

const BREADCRUMBS_WITHOUT_DROPDOWN = 3;

const isDropdownListType = (item: BreadcrumbItem | DropdownList): item is DropdownList => 'list' in item;

interface DropdownList {
    list: BreadcrumbItem[];
}
interface Props {
    breadcrumbs: BreadcrumbItem[];
    onClick: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    onShareClick?: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    onPublishClick?: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    mode?: 'viewer' | '';
    storage?: EStorageType;
    // Количество крошек, которые не будут скрываться в дропдаун
    breadcrumbsCount?: number;
}

const createList = (breadcrumbs: BreadcrumbItem[], breadcrumbsCount: number): (BreadcrumbItem | DropdownList)[] => {
    const showDropdown = breadcrumbs.length > breadcrumbsCount;

    if (!showDropdown) {
        return breadcrumbs;
    }

    const breadCrumbsList = [...breadcrumbs];
    const list = breadCrumbsList.splice(1, breadcrumbs.length - breadcrumbsCount);
    const [firstBreadCrumb, ...restBreadcrumbs] = breadCrumbsList;
    return [firstBreadCrumb, { list }, ...restBreadcrumbs];
};

export const BreadCrumbs = memo(
    ({
        breadcrumbs,
        onClick,
        onShareClick = noop,
        onPublishClick = noop,
        mode = '',
        storage,
        breadcrumbsCount = BREADCRUMBS_WITHOUT_DROPDOWN,
    }: Props): ReactElement => {
        const list = createList(breadcrumbs, breadcrumbsCount);

        return (
            <div className={classNames({ [styles.root]: true, [styles.root_public]: storage === EStorageType.public })}>
                {list.map((item, index) => {
                    const isActive = index === list.length - 1;
                    const breadCrumbMode = mode === 'viewer' ? (index === 0 ? 'light' : 'bold') : '';

                    return (
                        <div
                            key={isDropdownListType(item) ? 'list' : item.id}
                            className={classNames(styles.item, {
                                [styles.item_active]: isActive,
                                [styles.item_more]: isDropdownListType(item),
                                [styles.item_only]: list.length === 1,
                                [styles[`item_${breadCrumbMode}`]]: breadCrumbMode,
                            })}
                        >
                            {isDropdownListType(item) ? (
                                <BreadcrumbsDropdown onClick={onClick} list={item.list} />
                            ) : (
                                <Breadcrumb
                                    breadcrumb={item}
                                    isActive={isActive}
                                    onClick={onClick}
                                    onPublishClick={onPublishClick}
                                    onShareClick={onShareClick}
                                    data-qa-name={item.text}
                                />
                            )}
                            {!isActive && (
                                <div className={styles.arrow}>
                                    <IconArrow />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
);
BreadCrumbs.displayName = 'Breadcrumbs';
